/* RGB PixelGlow standalone one-curtain variant controls.
   Purpose: make each Size & LED Count radio update price, variant ID, quantity
   subtotal and existing Shopify payment form — no bundle or Pack Size UI.
   Intercepts PixelGlow's size-radio change before Dawn's AJAX section refresh,
   which was leaving the HTML radios, variant ID and displayed price out of sync.
   All other Shopify products retain native Dawn variant handling.
*/
(() => {
  const install = (selects) => {
    if (selects.dataset.rgbPgInteractionReady === '1') return;
    const productInfo = selects.closest('product-info');
    if (!productInfo || productInfo.dataset.url?.indexOf('/pixelglow-rgb-smart-curtain-lights') === -1) return;
    const raw = productInfo.querySelector('[data-rgb-pg-single-variants]');
    if (!raw) return;
    let variants;
    try { variants = JSON.parse(raw.textContent || '[]'); } catch (_) { return; }
    if (!Array.isArray(variants) || variants.length === 0) return;
    const bySize = new Map(variants.filter(x => x.available && x.size).map(x => [x.size, x]));
    if (!bySize.size) return;
    const variantsById = new Map(variants.map(x => [String(x.id),x]));
    const productForm = productInfo.querySelector('product-form');
    const actualForm = productForm?.querySelector('form[data-type="add-to-cart-form"]');
    const quantity = productInfo.querySelector('.product-form__quantity input[name="quantity"]');
    const priceRoot = productInfo.querySelector('[id^="price-"]');
    const totalPanel = productInfo.querySelector('[data-rgb-pg-single-totals]');
    if (!actualForm || !priceRoot || !quantity) return;
    const currency = raw.dataset.currency || window.Shopify?.currency?.active || 'GBP';
    const formatMoney = cents => {
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-GB',
          {style:'currency',currency,minimumFractionDigits:2,maximumFractionDigits:2})
          .format(Number(cents) / 100);
      } catch (_) { return '£' + (Number(cents)/100).toFixed(2); }
    };
    const checkedSize = () => selects.querySelector('.rgb-pg-size-only input[type="radio"]:checked');
    let active = null;

    const showTotals = () => {
      if (!active) return;
      const amount = Math.max(Number(quantity.min)||1, Number.parseInt(quantity.value,10)||1);
      const sub = Number(active.price)*amount;
      setPrice(active);
      if(totalPanel) {
        const label=totalPanel.querySelector('[data-rgb-pg-size-label]');
        const total=totalPanel.querySelector('[data-rgb-pg-total]');
        const each=totalPanel.querySelector('[data-rgb-pg-each]');
        if(label)label.textContent=active.size+' · '+amount+(amount===1?' curtain':' curtains');
        if(total)total.textContent=formatMoney(sub);
        if(each)each.textContent=formatMoney(active.price)+' each';
      }
    };
    const setPrice = v => {
      const holder=priceRoot.querySelector('.price');
      const standard=priceRoot.querySelector('.price__regular .price-item--regular');
      const sale=priceRoot.querySelector('.price__sale .price-item--last');
      const old=priceRoot.querySelector('.price__sale s.price-item');
      const amount=Math.max(Number(quantity.min)||1,Number.parseInt(quantity.value,10)||1);
      const formatted=formatMoney(Number(v.price)*amount);
      if(standard)standard.textContent=formatted;
      if(sale)sale.textContent=formatted;
      if(holder){
        const onSale=Number(v.compareAt)>Number(v.price);
        holder.classList.toggle('price--on-sale',onSale);
        holder.classList.toggle('price--sold-out',!v.available);
        if(old && onSale)old.textContent=formatMoney(Number(v.compareAt)*amount);
      }
      let caption=priceRoot.querySelector('[data-rgb-pg-headline-caption]');
      if(!caption){
        caption=document.createElement('p');
        caption.setAttribute('data-rgb-pg-headline-caption','');
        caption.style.margin='.45rem 0 .2rem';
        caption.style.fontSize='1.08rem';
        caption.style.lineHeight='1.5';
        caption.style.color='rgba(238,231,255,.78)';
        priceRoot.appendChild(caption);
      }
      caption.hidden=amount<=1;
      caption.textContent=amount>1
        ? `Total for ${amount} curtains · ${formatMoney(v.price)} per curtain`
        : '';
    };
    const setFormVariant = v => {
      productInfo.querySelectorAll('form[id^="product-form-"]').forEach(form => {
        const input=form.querySelector('input[name="id"]');
        if (!input) return;
        if(String(input.value)!==String(v.id)) {
          input.value=String(v.id);
          input.dispatchEvent(new Event('change',{bubbles:true}));
        }
        input.disabled=!v.available;
      });
      if(productForm?.toggleSubmitButton) {
        productForm.toggleSubmitButton(!v.available, v.available ? undefined : (window.variantStrings?.soldOut || 'Sold out'));
      } else {
        const submit=actualForm.querySelector('button[type="submit"][name="add"]');
        if(submit) submit.disabled=!v.available;
      }
      const selected=selects.querySelector('[data-selected-variant]');
      if(selected) {
        try {
          const existing=JSON.parse(selected.textContent||'{}');
          selected.textContent=JSON.stringify({...existing,id:v.id,price:v.price,compare_at_price:v.compareAt,available:v.available,option1:v.size,option2:'USB',option3:'1 Curtain'});
        } catch (_) {}
      }
      if (v.quantityMin && Number(quantity.min)!==Number(v.quantityMin)) {
        quantity.min=String(v.quantityMin);
        quantity.dataset.min=String(v.quantityMin);
      }
      if (v.quantityMax!=null)quantity.max=String(v.quantityMax);
      else quantity.removeAttribute('max');
    };
    const updateURL = v => {
      try {
        const u=new URL(window.location.href);
        u.searchParams.set('variant',String(v.id));
        window.history.replaceState(window.history.state,'',u.toString());
        const share=productInfo.querySelector('share-button');
        share?.updateUrl?.(u.toString());
      } catch (_) {}
    };
    const select = (size, updateHistory = true) => {
      const v=bySize.get(size);
      if(!v) return false;
      const radio=[...selects.querySelectorAll('.rgb-pg-size-only input[type="radio"]')].find(r=>r.value===size);
      if(!radio)return false;
      if(!radio.checked)radio.checked=true;
      active=v;
      setFormVariant(v);
      setPrice(v);
      showTotals();
      if(updateHistory)updateURL(v);
      return true;
    };
    // Capture, before Dawn's change subscriber sends an unrelated AJAX section
    // request. This avoids discarded responses and stale variant form IDs.
    selects.addEventListener('change',event=>{
      const input=event.target;
      if(!input?.matches?.('.rgb-pg-size-only input[type="radio"]'))return;
      event.stopImmediatePropagation();
      event.stopPropagation();
      select(input.value,true);
    },true);
    // Update subtotal as the native Shopify quantity +/- or typed input changes.
    quantity.addEventListener('input',showTotals);
    quantity.addEventListener('change',showTotals);
    productInfo.querySelectorAll('.product-form__quantity button.quantity__button').forEach(btn=>{
      btn.addEventListener('click',()=>window.setTimeout(showTotals,0));
    });
    actualForm.addEventListener('submit',event=>{
      if(!active)return;
      const input=actualForm.querySelector('input[name="id"]');
      if(String(input?.value)!==String(active.id)||!active.available){
        event.preventDefault();
        event.stopImmediatePropagation();
        setFormVariant(active);
        showTotals();
      }
    },true);
    const selected=checkedSize();
    if(!select(selected?.value||'1 m / 400 LEDs',false)) {
      const first=selects.querySelector('.rgb-pg-size-only input[type="radio"]');
      if(first)select(first.value,false);
    }
    selects.dataset.rgbPgInteractionReady='1';
  };
  const init=()=>document.querySelectorAll('variant-selects').forEach(install);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});
  else init();
  document.addEventListener('shopify:section:load',init);
})();
